import React from "react";
import { ModalType } from "../../../config/ModalTypes";
import { useAiChat } from "../../../hooks/useAiChat";
import useModal from "../../../hooks/useModal";
import { getCachedAnalysisResult, loadAnalysisResult } from "../../../state/ai/analysisResults";
import { mark, timer } from "../../../state/ai/runMetrics";
import { ChatMessage, Skill } from "../../../types";
import { AiDealAnalysisResult, AiDealAnalysisResultSchema, ANALYSIS_SEVERITY, AnalysisFinding, SortedAiDealAnalyisResult } from "../../../types/db/schema/ai/analysis.schema";
import { hasAnalysisSubmit, isPersistedMessage, resolveContainerFileId } from "../../../util/containerFiles";
import { getExcelVersion } from "../../../util/versioning";
import { AccordionPage, AccordionPageTitle } from "../accordion/AccordionPage";
import Button from "../buttons/Button";
import PageLoader from "../loader/PageLoader";
import Pill from "../pill/Pill";
import "./DealAnalysis.css";
import MessageTokens from "./tokens/MessageTokens";

interface DealAnalysisResult {
  data: SortedAiDealAnalyisResult | null,
  error: string | null
}

type AnalysisSource =
  | { kind: "json", value: unknown }
  | { kind: "file", fileId: string };

interface FileState {
  loading: boolean;
  data: AiDealAnalysisResult | null;
  error: string | null;
}

const PARSE_ERROR = "Failed to parse analysis result. If the issue persists, reach out with your model.";

const loadError = (reason: string) =>
  `The analysis could not be loaded: ${reason}. If the issue persists, reach out with your model.`;

const findAnalysisSource = (chatHistory: ChatMessage[] | undefined): AnalysisSource | null => {
  for (let i = (chatHistory?.length ?? 0) - 1; i >= 0; i--) {
    const c = chatHistory![i];

    if (c.role !== "assistant" || !c.content?.length) {
      continue;
    }

    if (isPersistedMessage(c) && hasAnalysisSubmit(c.content)) {
      // The file can sit in an earlier message than the submit call, so resolve it
      // the same way the tool did rather than looking at this message alone.
      const fileId = resolveContainerFileId(chatHistory!, i);

      if (fileId) {
        return { kind: "file", fileId };
      }
    }

    for (let u = c.content.length - 1; u >= 0; u--) {
      const b = c.content[u];

      if (b.type !== "text") {
        continue;
      }

      try {
        const parsed = JSON.parse(b.text);
        if (parsed) return { kind: "json", value: parsed };
      }
      catch { }

      break;
    }
  }

  return null;
}

const sortAnalysisResult = (result: AiDealAnalysisResult): SortedAiDealAnalyisResult => ({
  dealSummary: result.deal_summary,
  findings: result.findings.reduce((acc, finding) => {
    if (!acc[finding.severity]) {
      acc[finding.severity] = [];
    }
    acc[finding.severity].push(finding);
    return acc;
  }, {} as Record<ANALYSIS_SEVERITY, AnalysisFinding[]>),
  overallAssessment: result.analysis_summary.overall_assessment ?? "",
  totalFindings: result.analysis_summary.total_findings
});

interface FindingGroupProps {
  open?: boolean;
  severity: ANALYSIS_SEVERITY;
  findings: AnalysisFinding[];
}

const SeverityTitle = (props: { severity: ANALYSIS_SEVERITY, count: number }) => {

  const {
    severity,
    count
  } = props;

  let title = "";
  let fg = "";
  let bg = "";

  switch (severity) {
    case ANALYSIS_SEVERITY.CRITICAL:
      title = "Critical Issues";
      bg = "var(--bs-danger)";
      fg = "white";
      break;
    case ANALYSIS_SEVERITY.CAUTION:
      title = "Caution Flags";
      bg = "var(--bs-warning)";
      fg = "black";
      break;
    case ANALYSIS_SEVERITY.OPPORTUNITY:
      title = "Optimization Opportunities";
      bg = "var(--bs-info)";
      fg = "black";
      break;
    case ANALYSIS_SEVERITY.BEST_PRACTICE:
      title = "Best Practices";
      bg = "var(--bs-success)";
      fg = "white";
      break;
    default:

      title = severity;
      bg = "var(--teal-100)";
      fg = "var(--teal-800)";
      break;
  }

  return (
    <div className="d-flex flex-row gap-2">
      <Pill background={bg} foreground={fg}>
        <span>
          {count}
        </span>
      </Pill>
      <AccordionPageTitle>
        {title}
      </AccordionPageTitle>
    </div>
  )
}

const Finding = (props: { finding: AnalysisFinding }) => {

  const version = getExcelVersion();
  const showModal = useModal();

  const [expanded, setExpanded] = React.useState<boolean>(false);

  const { finding } = props;

  const group = (t: string, c: string) => (
    !!c && (
      <div className="d-flex flex-column">
        <strong>
          {t}
        </strong>
        <span>
          {c}
        </span>
      </div>
    )
  )

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex flex-column gap-3 p-3 border rounded">
        <div className="d-flex flex-column gap-2">
          <strong style={{ lineHeight: 1.1 }}>
            {finding.title}
          </strong>
          <Pill size="small" background="var(--teal-100)" foreground="var(--teal-800)">
            <span className="text-uppercase">
              {finding.category}
            </span>
          </Pill>
        </div>
        <div style={{ width: "100%", height: "1px", backgroundColor: "var(--gray-100)" }}  >
        </div>
        <span>
          {finding.description}
        </span>
        {
          expanded && (
            <div className="d-flex flex-column gap-3">
              {group("Recommendation:", finding.recommendation)}
              {group("Benchmark Context:", finding.benchmark_context)}
              {group("Value Summary:", finding.value_summary)}
              {group("Impact:", finding.impact)}
            </div>
          )
        }
        <div style={{ width: "100%", height: "1px", backgroundColor: "var(--gray-100)" }}  >
        </div>
        <div className="d-flex flex-row gap-2">
          {
            version.isExcel && finding.navigation && (
              <Button
                text="Show in Excel"
                icon="eye"
                color="var(--teal-600)"
                onClick={async () => await Excel.run(async (context) => {
                  const sheet = context.workbook.worksheets.getItemOrNullObject(finding.navigation!.sheet);
                  await context.sync();

                  if (sheet.isNullObject) {

                    showModal({
                      text: `Invalid reference: ${finding.navigation!.sheet}!${finding.navigation!.address}. The sheet does not exist in the workbook.`,
                      type: ModalType.Error
                    })

                    return;
                  }

                  const range = sheet.getRange(finding.navigation!.address);
                  range.select();
                  await context.sync();
                })
                }
              />
            )

          }
          <Button
            text={expanded ? "Hide details" : "Show details"}
            icon={expanded ? "x" : "info-circle"}
            color="var(--teal-100)"
            onClick={() => setExpanded((e) => !e)}
          />
        </div>
      </div>
      <div style={{ width: "100%", height: "1px", backgroundColor: "var(--gray-100)" }}  >
      </div>
    </div >
  )
}
const FindingGroup = (props: FindingGroupProps) => {

  const { findings, severity } = props;

  return (
    <AccordionPage title={<SeverityTitle severity={severity} count={findings?.length} />} open={props.open}>
      <div className="d-flex flex-column gap-2">
        {
          findings.map((finding, index) => (
            <Finding key={index} finding={finding} />
          ))
        }
      </div>
    </AccordionPage>
  )
}

const DOLLAR_FORMAT_MAP: [number, string][] = [
  [1e9, "B"],
  [1e6, "M"],
  [1e3, "K"],
];

export default function DealAnalysis(props: { skill: string, skillMeta: Skill }) {

  const { chatHistory, isLoading, thinkingSince } = useAiChat(props);

  const source = React.useMemo(() => findAnalysisSource(chatHistory), [chatHistory]);

  const fileId = source?.kind === "file" ? source.fileId : null;

  const [fileState, setFileState] = React.useState<FileState>({
    loading: false,
    data: null,
    error: null
  });

  // Keyed on the history as well as the file, so a load that failed once can
  // recover: by the time the next turn lands the tool has usually cached the
  // very same file, and without this the first failure would stick for good.
  React.useEffect(() => {
    let active = true;

    const cached = fileId ? getCachedAnalysisResult(fileId) : undefined;

    if (!fileId || cached) {
      setFileState({ loading: false, data: cached ?? null, error: null });
    }
    else {
      setFileState({ loading: true, data: null, error: null });

      const loadDone = timer(props.skill, "panel:loadResult");

      loadAnalysisResult(fileId, props.skill).then(res => {
        loadDone({ ok: res.ok });

        if (!active) return;

        switch (res.ok) {
          case true:
            setFileState({ loading: false, data: res.data, error: null });
            break;

          case false:
            setFileState({ loading: false, data: null, error: loadError(res.error) });
            break;
        }
      });
    }

    return () => { active = false };
  }, [fileId, chatHistory]);

  const analysis: DealAnalysisResult = React.useMemo(() => {
    if (!chatHistory?.length) {
      return {
        data: null,
        error: null
      }
    }

    if (source?.kind === "file") {
      if (fileState.error) {
        return { data: null, error: fileState.error };
      }

      if (!fileState.data) {
        return { data: null, error: null };
      }

      return { data: sortAnalysisResult(fileState.data), error: null };
    }

    const result = AiDealAnalysisResultSchema.safeParse(source?.kind === "json" ? source.value : null);

    if (!result.success) {
      return {
        data: null,
        error: PARSE_ERROR
      }
    }

    return {
      data: sortAnalysisResult(result.data),
      error: null
    }
  }, [chatHistory, source, fileState]);

  React.useEffect(() => {
    if (analysis?.data) mark(props.skill, "panel:rendered", { findings: analysis.data.totalFindings });
    else if (analysis?.error) mark(props.skill, "panel:error");
  }, [analysis?.data, analysis?.error]);

  if (isLoading || fileState.loading) {
    return <PageLoader />;
  }

  if (!analysis?.data) {
    return (
      <div className="ps-sm-4 pe-sm-4 pe-3 ps-3 pt-4">
        {
          analysis?.error
            ? (
              <span className="text-error">
                {analysis.error}
              </span>
            )
            : (
              <div className="d-flex flex-column gap-2">
                <h6 className="text-muted analysis-description">
                  {props.skillMeta?.description || "No existing Analysis."}
                </h6>
              </div>

            )
        }
      </div>
    )
  }

  const dollar = (v: number | string): string => {
    const n = Number(v) || 0;
    if (!n) return "$0";

    const abs = Math.abs(n);
    let resultValue = abs;
    let suffix = "";

    for (const [threshold, s] of DOLLAR_FORMAT_MAP) {
      if (abs >= threshold) {
        resultValue = abs / threshold;
        suffix = s;
        break;
      }
    }

    const formatted = resultValue.toLocaleString(undefined, {
      maximumFractionDigits: 2
    });

    return `${n < 0 ? "-" : ""}$${formatted}${suffix}`;
  };

  const percent = (v: number | string) => {
    const n = (Number(v) || 0) * 100;
    return `${n.toFixed(2)}%`;
  }

  const multiple = (v: number | string) => `${Number(v).toFixed(1)}x`;

  const card = (title: string, value: number | string | undefined | null, formatter: (v: any) => string, color?: string) => {
    if (value === undefined || value === null) {
      return null;
    }

    return (
      <div className="d-flex flex-column gap-0 p-2 border rounded w-100" >
        <strong className="font-s text-uppercase" style={{ color: "var(--gray-400)" }}>{title}</strong>
        <strong style={{ color }}>
          {formatter(value)}
        </strong>
      </div>

    )
  }

  const title = analysis.data?.dealSummary?.name;
  const subtitle = analysis.data?.dealSummary?.unit_count ? `Units: ${analysis.data.dealSummary.unit_count}` : undefined;
  const findings = Object.entries(analysis.data.findings);

  return (
    <div className="d-flex flex-column gap-3" style={{ flexGrow: 1, minHeight: 0 }}>
      <div className="d-flex gap-5 flex-column ps-sm-4 pe-sm-4 ps-3 pe-3 pt-4 pb-2" style={{ overflowY: "auto", flexGrow: 1, minHeight: 0 }}>
        <div className="d-flex flex-column gap-4">
          <div className="d-flex flex-column">
            <MessageTokens message={chatHistory?.[chatHistory.length - 1]} />
            <h4 className="m-0 p-0">{title ?? "Overall Assessment"}</h4>
            {subtitle && <span className="font-code">{subtitle}</span>}
          </div>
          <div className="d-flex flex-column">
            <h6>Deal Summary</h6>
            <span>
              {analysis.data.overallAssessment}
            </span>
          </div>
          <div className="d-flex flex-column gap-2">
            <h6>Pricing</h6>
            <div className="card-grid gap-3">
              {card("Purchase Price", analysis.data.dealSummary?.purchase_price, dollar)}
              {card("Exit Price", analysis.data.dealSummary?.exit_price, dollar)}
              {card("Entry Cap", analysis.data.dealSummary?.entry_cap, percent)}
              {card("Exit Cap", analysis.data.dealSummary?.exit_cap, percent)}
            </div>
          </div>
          <div className="d-flex flex-column gap-2">
            <h6>Returns</h6>
            <div className="card-grid gap-3">
              {card("Cash-on-Cash", analysis.data.dealSummary?.cash_on_cash, percent, "var(--bs-success)")}
              {card("Leveraged IRR", analysis.data.dealSummary?.leveraged_irr, percent, "var(--bs-success)")}
              {card("Avg Annual Return", analysis.data.dealSummary?.avg_annual_return, percent, "var(--bs-success)")}
              {card("Equity Multiple", analysis.data.dealSummary?.equity_multiple, multiple, "var(--bs-success)")}
            </div>
          </div>
        </div>
        <div className="d-flex flex-column gap-4">
          <div className="d-flex flex-row align-items-center gap-2">
            <h4 className="m-0 p-0">
              Findings
            </h4>
            <Pill background="#CCC" foreground="black" round>
              {analysis.data.totalFindings}
            </Pill>
          </div>
          {
            findings?.length
              ? findings.map(([severity, findings], index) => (
                <FindingGroup
                  open={index === 0}
                  key={severity}
                  findings={findings}
                  severity={severity as ANALYSIS_SEVERITY}
                />
              ))
              : <span>No fingings.</span>
          }
        </div>
      </div>
    </div >
  )
}